import api from './api';

// Types
export interface Parceiro {
  id: number;
  nome: string;
  categoria: string;
  desconto: string;
  logo: string;
  descricao: string;
  endereco?: string;
  telefone?: string;
  website?: string;
  destaque: boolean;
  status: string;
  created_at: string;
  // Fundador Pagali (Passo 3 do plano)
  is_fundador?: 0 | 1 | boolean;
  cidade_fundador?: string | null;
  badge_visivel?: 0 | 1 | boolean;
  posicao_destaque?: number | null;
  data_fundador_inicio?: string | null;
  data_fundador_expiracao?: string | null;
}

export interface HubMe {
  is_embaixador: boolean;
  embaixador: {
    id: number;
    categoria: string;
    tier: 'bronze' | 'prata' | 'ouro';
    estado: string;
    badge_icone: string;
    early_access: 0 | 1;
    pontos_mensais: number;
  } | null;
}

export interface NivelBeneficio {
  id: number;
  nome: string;
  pontos_minimos: number;
  pontos_maximos: number;
  beneficios: string[];
  cor: string;
  icone: string;
  cashback_percent?: number;
  desconto_parceiros_percent?: number;
}

export interface BeneficiosData {
  total_pontos: number;
  nivel_atual: NivelBeneficio;
  proximo_nivel: NivelBeneficio | null;
  pontos_para_proximo: number;
  progresso_percentual: number;
  todos_niveis?: NivelBeneficio[];
}

export interface PontosHistorico {
  id: number;
  pontos: number;
  tipo: string;
  descricao: string;
  referencia_id?: string;
  created_at: string;
}

export interface ReferralData {
  codigo: string;
  link?: string;
  total_convites: number;
  total_bonus: number;
  bonus_por_convite?: number;
  bonus_convidado?: number;
  convites: ReferralConvite[];
}

export interface ReferralConvite {
  id: number;
  convidado_nome: string;
  convidado_email: string;
  status: string;
  bonus: number;
  created_at: string;
  accepted_at?: string;
}

export interface MiniApp {
  id: number;
  nome: string;
  descricao: string;
  icone: string;
  categoria: string;
  url: string;
  destaque: boolean;
  total_instalacoes: number;
  status: string;
  preco: number;
  preco_original?: number;
  gratuito: boolean;
  purchased?: boolean;
  purchased_at?: string;
}

export interface Novidade {
  id: number;
  titulo: string;
  resumo: string;
  conteudo?: string;
  imagem?: string;
  link?: string;
  categoria: 'feature' | 'update' | 'promo' | string;
  destaque: boolean | number;
  published_at: string;
}

export interface FeatureRequest {
  id: number;
  titulo: string;
  descricao: string;
  categoria: string;
  status: string;
  total_votos: number;
  votou: boolean | number;
  created_at: string;
}

export interface Recurso {
  id: number;
  titulo: string;
  descricao: string;
  conteudo?: string;
  tipo: 'faq' | 'guia' | 'video' | 'contacto' | 'quick_action' | string;
  url?: string;
  icone?: string;
  categoria: string;
  ordem: number;
}

export interface Missao {
  id: number;
  titulo: string;
  descricao: string | null;
  icone: string;
  tipo_evento: string;
  objetivo: number;
  pontos_recompensa: number;
  bonus_cve: number;
  categoria: string | null;
  dificuldade: 'facil' | 'media' | 'dificil';
  data_inicio: string | null;
  data_fim: string | null;
  progresso_atual: number;
  estado: 'em_progresso' | 'completa' | 'reclamada' | 'expirada';
  data_conclusao: string | null;
}

export interface HubDashboard {
  total_pontos: number;
  nivel: string;
  total_referrals: number;
  apps_instalados: number;
  parceiros_destaque: Parceiro[];
  novidades_recentes: Novidade[];
}

class HubService {
  // ============================================
  // PARCEIROS
  // ============================================

  async getParceiros(categoria?: string, destaque?: boolean): Promise<Parceiro[]> {
    const params = new URLSearchParams();
    if (categoria && categoria !== 'Todos') params.append('categoria', categoria);
    if (destaque) params.append('destaque', 'true');

    const query = params.toString();
    const url = `/client/hub/parceiros${query ? `?${query}` : ''}`;

    const response = await api.get<{
      success: boolean;
      data?: Parceiro[];
      error?: string;
    }>(url);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar parceiros');
  }

  async getParceirosCategorias(): Promise<string[]> {
    const response = await api.get<{
      success: boolean;
      data?: string[];
      error?: string;
    }>('/client/hub/parceiros/categorias');
    return response.data.success && response.data.data ? response.data.data : ['Todos'];
  }

  async getAppsCategorias(): Promise<string[]> {
    const response = await api.get<{
      success: boolean;
      data?: string[];
      error?: string;
    }>('/client/hub/apps/categorias');
    return response.data.success && response.data.data ? response.data.data : ['Todos'];
  }

  async getParceiroById(id: number): Promise<Parceiro> {
    const response = await api.get<{
      success: boolean;
      data?: Parceiro;
      error?: string;
    }>(`/client/hub/parceiros/${id}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar parceiro');
  }

  // ============================================
  // BENEFICIOS / PONTOS
  // ============================================

  async getBeneficios(): Promise<BeneficiosData> {
    const response = await api.get<{
      success: boolean;
      data?: BeneficiosData;
      error?: string;
    }>('/client/hub/beneficios');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar beneficios');
  }

  async getPontosHistorico(limit = 20, offset = 0): Promise<PontosHistorico[]> {
    const response = await api.get<{
      success: boolean;
      data?: PontosHistorico[];
      error?: string;
    }>(`/client/hub/beneficios/historico?limit=${limit}&offset=${offset}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar historico');
  }

  // ============================================
  // REFERRAL
  // ============================================

  async getReferral(): Promise<ReferralData> {
    const response = await api.get<{
      success: boolean;
      data?: ReferralData;
      error?: string;
    }>('/client/hub/referral');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar dados de referral');
  }

  async aplicarCodigoReferral(codigo: string): Promise<{ bonus: number; message: string }> {
    const response = await api.post<{
      success: boolean;
      data?: { bonus: number; message: string };
      error?: string;
    }>('/client/hub/referral/aplicar', { codigo });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao aplicar codigo');
  }

  // ============================================
  // MINI-APPS
  // ============================================

  async getApps(categoria?: string): Promise<MiniApp[]> {
    const params = new URLSearchParams();
    if (categoria && categoria !== 'Todos') params.append('categoria', categoria);

    const query = params.toString();
    const url = `/client/hub/apps${query ? `?${query}` : ''}`;

    const response = await api.get<{
      success: boolean;
      data?: MiniApp[];
      error?: string;
    }>(url);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar apps');
  }

  async getAppsPurchased(): Promise<MiniApp[]> {
    const response = await api.get<{
      success: boolean;
      data?: MiniApp[];
      error?: string;
    }>('/client/hub/apps/purchased');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar apps comprados');
  }

  async getPurchasedAppCodes(): Promise<string[]> {
    const response = await api.get<{
      success: boolean;
      data?: string[];
      error?: string;
    }>('/client/hub/apps/purchased/codes');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return []; // Return empty array on error to avoid blocking UI
  }

  async purchaseApp(appId: number): Promise<{ message: string; transaction_id?: string }> {
    const response = await api.post<{
      success: boolean;
      data?: { message: string; transaction_id?: string };
      error?: string;
    }>(`/client/hub/apps/${appId}/purchase`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao comprar app');
  }

  async openApp(appId: number): Promise<{ url: string }> {
    const response = await api.get<{
      success: boolean;
      data?: { url: string };
      error?: string;
    }>(`/client/hub/apps/${appId}/open`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao abrir app');
  }

  // ============================================
  // NOVIDADES
  // ============================================

  async getNovidades(categoria?: string, destaque?: boolean): Promise<Novidade[]> {
    const params = new URLSearchParams();
    if (categoria && categoria !== 'Todos') params.append('categoria', categoria);
    if (destaque) params.append('destaque', 'true');

    const query = params.toString();
    const url = `/client/hub/novidades${query ? `?${query}` : ''}`;

    const response = await api.get<{
      success: boolean;
      data?: Novidade[];
      error?: string;
    }>(url);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar novidades');
  }

  async getFeatures(): Promise<FeatureRequest[]> {
    const response = await api.get<{
      success: boolean;
      data?: FeatureRequest[];
      error?: string;
    }>('/client/hub/novidades/features');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar features');
  }

  async votarFeature(featureId: number): Promise<{ total_votos: number }> {
    const response = await api.post<{
      success: boolean;
      data?: { total_votos: number };
      error?: string;
    }>(`/client/hub/novidades/features/${featureId}/votar`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao votar');
  }

  // ============================================
  // RECURSOS
  // ============================================

  async getRecursos(categoria?: string): Promise<Recurso[]> {
    const params = new URLSearchParams();
    if (categoria && categoria !== 'Todos') params.append('categoria', categoria);

    const query = params.toString();
    const url = `/client/hub/recursos${query ? `?${query}` : ''}`;

    const response = await api.get<{
      success: boolean;
      data?: Recurso[];
      error?: string;
    }>(url);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar recursos');
  }

  // ============================================
  // DASHBOARD
  // ============================================

  // ============================================
  // HUB ME (perfil do user: embaixador, etc)
  // ============================================
  async getMe(): Promise<HubMe> {
    const response = await api.get<{ success: boolean; data?: HubMe }>('/client/hub/me');
    return response.data.data || { is_embaixador: false, embaixador: null };
  }

  // ============================================
  // MISSÕES & GAMIFICAÇÃO
  // ============================================
  async getMissoes(): Promise<Missao[]> {
    const response = await api.get<{ success: boolean; data?: Missao[] }>('/client/hub/missoes');
    return response.data.data || [];
  }

  async reclamarMissao(id: number): Promise<{ pontos_creditados: number; bonus_cve: number }> {
    const response = await api.post<{ success: boolean; data: { pontos_creditados: number; bonus_cve: number }; error?: string }>(
      `/client/hub/missoes/${id}/reclamar`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Erro ao reclamar missão');
    return response.data.data;
  }

  async getDashboard(): Promise<HubDashboard> {
    const response = await api.get<{
      success: boolean;
      data?: HubDashboard;
      error?: string;
    }>('/client/hub/dashboard');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar dashboard');
  }
}

export default new HubService();
